import { default as videojs } from 'video.js';
import { CinematicViewToggle } from '../adapters/video-js/components/cinematic-view-toggle';
import { Size } from '../analytics';
import { __PLANET_MEDIA_PLAYER_VERSION__ } from '../constants';
import { Environment, EnvironmentInfo } from '../environment';
import {
    IPlayerAdapter,
    MediaItem,
    MediaMetadataEventArgs,
    MediaPlayerSettings,
    MetaEventArgs,
    PlanetMediaPlayerEvents,
    PlanetMediaPlayerSetup,
    PlayerEvents,
    PlaylistController,
    PlaylistControllerEvents,
    ReadyEvent,
    StreamingMediaSource,
} from '../models';
import { IPlugin, PluginConfiguration } from '../plugin';
import { EventEmitterBase, Guid, Queue, TimeFormat } from '../utils';
import {
    AbstractVisibilityMonitor,
    VisibilityMonitorEvents,
    VisibilityMonitorIntersectionObserver,
    VisibilityMonitorState,
} from '../visibility-monitor';
import { ContentDuration } from './content-duration';
import { DateTimeUtils } from './date-time-utils';
import { ContentType, MediaControllerState, PlaybackSessionEvents, StateControllerEvents } from './enums';
import { MediaControllerStateSnapshot } from './media-controller-state-snapshot';
import {
    IContent,
    IMediaController,
    IPlaybackStats,
    IPlayerSettings,
    IQualityLevel,
    PlanetAutostartArgs,
    PlayerErrorArgs,
    PlayerPlaylistArgs,
    PlayerPlaylistChangeArgs,
    PlayerPlaylistItemChangeArgs,
    PlayerPositionArgs,
    PlayerReadyArgs,
    PlayerStateChangeArgs,
} from './models';
import { PlaybackSession } from './playback-session';
import { PlaybackSessionState } from './playback-session-state';
import { StateController } from './state-controller';

export abstract class BaseMediaController extends EventEmitterBase implements IMediaController {

    readonly version: string = __PLANET_MEDIA_PLAYER_VERSION__;

    protected adapter: IPlayerAdapter;

    protected metadata: MetaEventArgs;
    protected mediaMetadata: MediaMetadataEventArgs;

    protected containerId: string;

    protected playTriggered = false;

    protected isRemoved = false;
    protected timeUpdates = new Queue<number>();
    protected positionBeforePause = 0;

    protected pluginsConfig: PluginConfiguration[] = [];
    protected plugins: { [key: string]: IPlugin<any> } = {};

    protected interacted: boolean = false;

    protected config: PlanetMediaPlayerSetup;
    protected visibilityMonitor: AbstractVisibilityMonitor;
    protected state: MediaControllerState;
    protected stateController = new StateController();
    protected playlistController = new PlaylistController();
    protected playbackSession: PlaybackSession = null;

    protected id: string;

    protected _started: boolean;

    protected constructor(containerId: string, plugins: PluginConfiguration[] = []) {
        super();
        this.pluginsConfig = plugins;
        this.containerId = containerId;
        this.id = Guid.v1();
    }

    async setup(options: PlanetMediaPlayerSetup): Promise<void> {
        await this.init();
        let settings: MediaPlayerSettings = new MediaPlayerSettings();
        this.config = Object.assign(settings, options);
    }

    async load(playlist: MediaItem[] | MediaItem | string): Promise<MediaItem[]> {
        return await this.playlistController.load(playlist);
    }

    addPlugin(descriptor: PluginConfiguration): void {
        this.pluginsConfig.push(descriptor);
    }

    addPlugins(descriptors: PluginConfiguration[]): void {
        descriptors.forEach(d => this.addPlugin(d));
    }

    setPlaylistItem(item: MediaItem): Promise<MediaItem> {
        return new Promise(resolve => resolve(item));
    }

    async reload(): Promise<void> {
        await this.setPlaylistItem(this.playlistController.currentItem);
    }

    autoPlay(): void {
        let exit: boolean = false;
        this.once(PlanetMediaPlayerEvents.Error, (error: PlayerErrorArgs) => {
            if (error.code > 1 && error.code < 6) { // media errors, skip autoplay+retry
                exit = true;
            }
        });
        /**
         * https://developers.google.com/web/updates/2017/06/play-request-was-interrupted
         */
        const playPromise: Promise<void> = this.play();
        if (playPromise !== undefined && typeof playPromise.then === 'function') {
            playPromise.then(() => {
                const playbackSession: PlaybackSessionState = this.getPlaybackSession();
                if (!this.isRemoved && playbackSession) {
                    const event: PlanetAutostartArgs = {
                        content: playbackSession?.content,
                    };
                    this.emit(PlanetMediaPlayerEvents.Autostart, event);
                }
            }).catch((e) => {
                if (this.isNotAllowedError(e) && !exit) {
                    this.setMuted(true);
                    try {
                        this.play()?.catch((e_2) => {
                            this.handlePlayError(e_2);
                        });
                    } catch (e_2) {
                        this.handlePlayError(e_2);
                    }
                } else {
                    const playbackSession: PlaybackSessionState = this.getPlaybackSession();
                    const event: PlanetAutostartArgs = {
                        content: playbackSession?.content,
                        error: e,
                        attempts: 1,
                    };
                    this.emit(PlanetMediaPlayerEvents.AutostartFailed, event);
                    // stop the player to avoid buffer related errors
                    this.stop();
                }
            });
        }
    }

    private handlePlayError(e_2): void {
        const playbackSession: PlaybackSessionState = this.getPlaybackSession();
        if (this.isRemoved || !playbackSession) {
            return;
        }

        const event: PlanetAutostartArgs = {
            content: playbackSession?.content,
            error: e_2,
            attempts: 2,
        };

        if (this.isNotAllowedError(e_2)) {
            this.emit(PlanetMediaPlayerEvents.AutostartNotAllowed, event);
        } else {
            this.emit(PlanetMediaPlayerEvents.AutostartFailed, event);
        }
        this.stop();
    }

    getPositionBeforePause(): number {
        return this.positionBeforePause;
    }

    /**
     * Get position as HH:mm:ss
     */
    getPositionAsString(): string {
        return TimeFormat.format(this.getPosition());
    }

    /**
     * Get current playlist index
     */
    getPlaylistIndex(): number {
        return this.playlistController.currentIndex;
    }

    setState(state: MediaControllerState): void {
        this.stateController.state = state;
    }

    getState(): MediaControllerState {
        return this.stateController.state;
    }

    getConfig(): PlanetMediaPlayerSetup {
        return this.config;
    }

    createPlaylist(playlist: MediaItem): void {
        console.log('createPlaylist is not implemented');
    }

    getPlaylist(): MediaItem[] {
        return this.playlistController.playlist;
    }

    getPlaylistItem(index?: number): MediaItem {
        return this.playlistController.getItem(index);
    }

    log(...args: Array<any>): void {
        if (window.console && window['__PLANET_MEDIA_PLAYER_DEBUG_ENABLED__'] === true) {
            if (console.log) {
                if (console.log.apply) {
                    console.log.apply(console, args);
                }
            } else {
                console.log(args);
            }
        }
    }

    /**
     * Change index of playlist
     * @param index
     */
    playlistItem(index: number): void {
        this.playlistController.setCurrentIndex(index);
    }

    remove(dispose: boolean = true): void {
        this.emit(PlanetMediaPlayerEvents.BeforeRemove);
        this.visibilityMonitor.stop();
        if (this.playbackSession !== null) {
            this.playbackSession.destroy();
        }
        this.stateController.removeAllListeners();
        this.visibilityMonitor.removeAllListeners();
        this.playlistController.removeAllListeners();
        this.playbackSession = null;
        this.visibilityMonitor = null;
        this.playlistController = null;
        this.stateController = null;
        this.isRemoved = true;
        this.playTriggered = false;
    }

    getHtmlElementId(): string {
        return 'media-player-' + this.getId();
    }

    getId(): string {
        return this.id;
    }

    getPlaybackSession(): PlaybackSessionState {
        return this.playbackSession?.state;
    }

    async togglePlayback(): Promise<void> {
        if (this.getState() === MediaControllerState.Playing) {
            return this.pause();
        } else {
            return this.play();
        }
    }

    getContainer(): HTMLElement {
        return document.getElementById(this.containerId);
    }

    getEnvironment(): EnvironmentInfo {
        return Environment;
    }

    getCurrentPlaylistItem(): MediaItem {
        return this.playlistController?.currentItem;
    }

    getStateSnapshot(): MediaControllerStateSnapshot {
        const snapshot: MediaControllerStateSnapshot = new MediaControllerStateSnapshot();
        snapshot.state = this.getState();

        if (this.adapter) {
            snapshot.state = this.getState();
            snapshot.muted = this.getMuted();
            snapshot.visible = this.isVisible();
            if (snapshot.state === MediaControllerState.Playing) {
                snapshot.position = this.getPosition();
            } else {
                snapshot.position = this.getPositionBeforePause();
            }
            snapshot.duration = this.getDuration();
            snapshot.timestamp = DateTimeUtils.UTCNow;
            snapshot.currentItem = this.getCurrentPlaylistItem();
            snapshot.containerId = this.adapter.containerId;
            snapshot.currentSource = this.getCurrentSource();
            snapshot.autoplayEnabled = this.getConfig().autostart === true;
        }

        return snapshot;
    }

    isVisible(): boolean {
        return this.visibilityMonitor.visible;
    }

    supports(method: string): boolean {
        return method in this.adapter && typeof this.adapter[method] === 'function';
    }

    /**
     * Extracts the current frame as a base64 png image dataURL.
     * If width and height are not passed, size from getMediaMetadata() is used
     * @return string Return a CanvasRenderingContext2D with the current frame's data
     * @param width
     * @param height
     */
    getCurrentFrame(width?: number, height?: number): CanvasRenderingContext2D {
        const video: HTMLVideoElement = this.getContainer().querySelector('video');
        if (video) {
            const meta: Size = this.getMediaMetadata();
            if (meta) {
                const canvas: HTMLCanvasElement = document.createElement('canvas');
                const context: CanvasRenderingContext2D = canvas.getContext('2d');
                canvas.width = width ?? meta.width;
                canvas.height = height ?? meta.height;
                context.drawImage(video, 0, 0, meta.width, meta.height, 0, 0, canvas.width, canvas.height);
                return context;
            }
        }
        return null;
    }

    started(): boolean {
        return this._started;
    }

    abstract onDisplayClick(): void;

    abstract onDisplayDoubleClick(): void

    abstract addTextTrack(options: videojs.TextTrackOptions): void;

    private async init(): Promise<void> {
        const setupStartTime: number = Date.now();
        this.on(PlanetMediaPlayerEvents.Error, () => this.setState(MediaControllerState.Error));
        this.on(PlanetMediaPlayerEvents.SetupError, () => this.setState(MediaControllerState.Error));

        this.stateController.on(StateControllerEvents.StateChange, (event: PlayerStateChangeArgs) => {
            this.emit(PlanetMediaPlayerEvents.StateChange, event);
        });

        this.setState(MediaControllerState.Initializing);
        this.on(PlanetMediaPlayerEvents.UserInteraction, () => this.interacted = true);
        this.once(PlanetMediaPlayerEvents.PlaylistItem, () => {
            const event: PlayerReadyArgs = {
                type: PlanetMediaPlayerEvents.Ready,
                viewable: this.isVisible(),
                setupTime: Date.now() - setupStartTime,
            };
            this.emit(PlanetMediaPlayerEvents.Ready, event);
        });

        this.once(PlanetMediaPlayerEvents.Ready, () => {
            const container: HTMLElement = this.getContainer();
            this.visibilityMonitor.observe(container);
            this.visibilityMonitor.start();
            this.setState(MediaControllerState.Idle);
        });

        this.playlistController.on(PlaylistControllerEvents.PlaylistChange, (e: PlayerPlaylistChangeArgs) => {
            const event: PlayerPlaylistArgs = {
                playlist: e.playlist,
            };
            this.emit(PlanetMediaPlayerEvents.Playlist, (event));
        });

        this.playlistController.on(PlaylistControllerEvents.PlaylistItemChange, (e: PlayerPlaylistItemChangeArgs) => {
            this.setState(MediaControllerState.Loading);
            this.setPlaylistItem(e.item)
                .then((item: MediaItem) => {
                    const event: PlayerPlaylistItemChangeArgs = {
                        item: item,
                        index: e.index,
                    };
                    this.emit(PlanetMediaPlayerEvents.PlaylistItem, event);
                })
                .catch(error => {
                    console.warn(error);
                });
        });

        const container: HTMLElement = this.getContainer();
        this.visibilityMonitor = new VisibilityMonitorIntersectionObserver(container);

        this.visibilityMonitor.on(VisibilityMonitorEvents.VisibilityChange, event => {
            this.emit(PlanetMediaPlayerEvents.VisibilityChange, {...event, viewable: event.visible ? 1 : 0});
        });

        this.visibilityMonitor.on(VisibilityMonitorEvents.Hidden, (event: VisibilityMonitorState) => {
            this.emit(PlanetMediaPlayerEvents.Hidden, {...event, viewable: 1});
        });

        this.visibilityMonitor.on(VisibilityMonitorEvents.Visible, (event: VisibilityMonitorState) => {
            this.emit(PlanetMediaPlayerEvents.Visible, {...event, viewable: 0});
        });

        this.on(PlanetMediaPlayerEvents.DisplayClick, () => this.onDisplayClick());

        this.on(PlanetMediaPlayerEvents.DisplayDoubleClick, () => this.onDisplayDoubleClick());

        this.on(PlanetMediaPlayerEvents.PlaylistItem, (event: PlayerPlaylistItemChangeArgs) => {

            const autostart: boolean | 'viewable' | 'visible' = this.getConfig().autostart;
            const muted: boolean = this.getMuted();
            const visible: boolean = this.isVisible();
            const iFrame: boolean = Environment.Features.frame;
            const duration: number = this.getDuration();

            const item: MediaItem = event.item;
            let isDvr: boolean = false;

            // we check if the sources url contains the dvr parameter
            for (let source in item.sources) {
                source = source.toLowerCase();
                const tokens: string[] = source.split('?');
                const url: string = tokens.shift();
                const query: string = tokens.shift();
                if (query && typeof query === 'string' && query.includes('dvr')) {
                    isDvr = true;
                    break;
                }
            }

            const contentType: ContentType =
                isDvr ? ContentType.LiveDvr : (
                    duration === Infinity
                        ? ContentType.Live
                        : ContentType.Vod
                );

            const content: IContent = {
                index: event.index,
                title: event.item.title,
                mediaId: event.item.mediaid,
                contentId: event.item.contentId,
                duration: new ContentDuration(duration),
                playbackType: contentType,
                contentType: contentType,
            };

            const settings: IPlayerSettings = {
                autostart: autostart,
                muted: muted,
                visible: visible,
                iFrame: iFrame,
            };

            if (this.playbackSession !== null) {
                this.playbackSession.destroy();
                this.playbackSession = null;
            }

            const id: string = this.getId();
            this.playbackSession = new PlaybackSession(id);
            this.playbackSession.on('event', () => {
                const playbackStats: IPlaybackStats = this.getPlaybackStats();
                this.playbackSession.updateStats(playbackStats);
            });
            this.playbackSession.on(PlaybackSessionEvents.ContentLoaded, async (session: PlaybackSessionState) => {
                this.emit(PlanetMediaPlayerEvents.PlaybackSessionContentLoad, session);
            });
            this.playbackSession.on(PlaybackSessionEvents.SessionStarted, (session: PlaybackSessionState) => {
                this.emit(PlanetMediaPlayerEvents.PlaybackSessionStart, session);
            });
            this.playbackSession.on(PlaybackSessionEvents.SessionResumed, (session: PlaybackSessionState) => {
                this.emit(PlanetMediaPlayerEvents.PlaybackSessionResume, session);
            });
            this.playbackSession.on(PlaybackSessionEvents.Paused, (session: PlaybackSessionState) => {
                this.emit(PlanetMediaPlayerEvents.PlaybackSessionPause, session);
            });
            this.playbackSession.on(PlaybackSessionEvents.Seeked, (session: PlaybackSessionState) => {
                this.emit(PlanetMediaPlayerEvents.PlaybackSessionSeek, session);
            });
            this.playbackSession.on(PlaybackSessionEvents.SessionEnded, (session: PlaybackSessionState) => {
                this.emit(PlanetMediaPlayerEvents.PlaybackSessionEnd, session);
            });

            this.emit(PlanetMediaPlayerEvents.PlaybackSessionCreate, this.playbackSession);

            this.on(PlanetMediaPlayerEvents.FirstPlay, () => this.playbackSession.firstPlay());

            this.on(PlanetMediaPlayerEvents.FirstFrame, () => this.playbackSession.firstFrame());

            this.playbackSession.load(content, settings);

            if (this.config) {
                // Check if startTime was provided in the configuration
                // if it was, then we autostart playback at the specified time
                if (event.index === 0) {
                    const startTime: number = (this.config.starttime || event.item.startTime) || 0;
                    if (startTime > 0) {
                        this.config.autostart = 'viewable';
                        this.seek(startTime);
                    }
                } else {
                    const startTime: number = (event.item.startTime) || 0;
                    if (startTime > 0) {
                        this.config.autostart = 'viewable';
                        this.seek(startTime);
                    }
                }

                if (this.config.autostart !== true) {
                    // If autostart value is 'viewable'
                    // we wait for the player to be visible before starting the playback
                    if (this.config.autostart === 'viewable') {
                        if (this.isVisible()) {
                            this.autoPlay();
                        } else {
                            this.once(PlanetMediaPlayerEvents.Visible, (event: VisibilityMonitorState) => {
                                if (event.visible) {
                                    this.autoPlay();
                                }
                            });
                        }
                    }
                } else {
                    // autostart === true
                    // attempt autostart, this might fail due to browser restrictions.
                    this.autoPlay();
                }
            }
        });

        this.on(PlanetMediaPlayerEvents.Play, () => this.playTriggered = true);

        this.on(PlanetMediaPlayerEvents.Idle, () => this.setState(MediaControllerState.Idle));
        this.on(PlanetMediaPlayerEvents.Playing, () => this.setState(MediaControllerState.Playing));
        this.on(PlanetMediaPlayerEvents.Pause, () => {
            const updates: number[] = this.timeUpdates.getItems().reverse();
            const lastTrackedPosition: number = updates[0];
            let pausePosition: number = lastTrackedPosition;
            for (let i = 1; i < updates.length; i++) {
                let tmp: number = updates[i];
                if (Math.abs(lastTrackedPosition - tmp) > 1) {
                    pausePosition = tmp;
                    break;
                }
            }
            this.positionBeforePause = pausePosition;
            this.setState(MediaControllerState.Paused);
        });
        this.on(PlanetMediaPlayerEvents.Seeking, () => {
            this.playbackSession.seekStart();
            this.setState(MediaControllerState.Paused);
        });
        this.on(PlanetMediaPlayerEvents.Seeked, (e: PlayerPositionArgs) => this.playbackSession.seekEnd(e.position));
        this.on(PlanetMediaPlayerEvents.Buffer, () => this.setState(MediaControllerState.Buffering));

        this.on(PlanetMediaPlayerEvents.SetupError, () => this.setState(MediaControllerState.Error));
        this.on(PlanetMediaPlayerEvents.Error, () => this.setState(MediaControllerState.Error));

        this.on(PlanetMediaPlayerEvents.Complete, () => {
            this.playbackSession.end(true);
            // check if there are more items in the playlist
            if (this.playlistController.hasNext()) {
                this.playlistController.next();
            } else {
                this.emit(PlanetMediaPlayerEvents.PlaylistComplete, {
                    playlist: this.playlistController.playlist,
                });
            }

        });
        this.on(PlanetMediaPlayerEvents.Remove, () => this.setState(MediaControllerState.Disposing));
        this.on(PlanetMediaPlayerEvents.Time, (e) => {
            if (this.timeUpdates.size > 2) {
                this.timeUpdates.dequeue();
            }
            const position: number = this.getPosition();
            this.timeUpdates.enqueue(position);
            if (this.playbackSession) {
                const stats: IPlaybackStats = this.getPlaybackStats();
                this.playbackSession.updateStats(stats);
                const state: MediaControllerState = this.getState();
                if (state == MediaControllerState.Playing) {
                    this.playbackSession.updatePosition(e.position, state);
                }
            }
        });

        this.on(PlanetMediaPlayerEvents.StateChange, (event: PlayerStateChangeArgs) => {
            const oldState: MediaControllerState = event.oldState;
            const newState: MediaControllerState = event.newState;
            if (newState === MediaControllerState.Playing) {
                if (this.playbackSession !== null && this.playbackSession.state.completed) {
                    this.playbackSession.reset();
                    this.emit(PlanetMediaPlayerEvents.PlaybackSessionCreate, this.playbackSession);
                }
                const stateSnapshot: MediaControllerStateSnapshot = this.getStateSnapshot();
                this.playbackSession.start(stateSnapshot.position);
            } else {
                if (oldState === MediaControllerState.Playing) {
                    const stateSnapshot: MediaControllerStateSnapshot = this.getStateSnapshot();
                    this.playbackSession.pause(stateSnapshot.position, newState);
                }
            }
        });

        PlayerEvents.map((event: PlanetMediaPlayerEvents) => this.on(event, (...args: Array<any>) => {
            if (event !== PlanetMediaPlayerEvents.PlayerEvent) {
                this.emit(PlanetMediaPlayerEvents.PlayerEvent, {
                    sourceEvent: {type: event, args},
                });
            }
        }));

        this.pluginsConfig.forEach(<T>(d: PluginConfiguration) => {
            let plugin: IPlugin<T> = d.factory();
            let key: string = d.type || d.name;
            this.plugins[key] = plugin;
            plugin.setup(this, d.options);
        });
    }

    private isNotAllowedError(e: { code?: number, name?: string, message?: string }): boolean {
        const result: boolean = e.code === 20 || e.code === 0 || (e.code && 'name' in e && e.name === 'NotAllowedError');
        console.debug('is not allowed error?', result, e.code, e.message, e.name);
        return result;
    }

    abstract addClass(className: string): void;

    abstract removeClass(className: string): void;

    abstract setPoster(image: string): void;

    abstract setTitle(title: string): void;

    abstract setDescription(title: string): void;

    abstract getTextTracks(): TextTrackList;

    abstract getMeta(): MetaEventArgs;

    abstract getMediaMetadata(): Size;

    abstract getProvider(): string;

    abstract getQualityLevels(): IQualityLevel[];

    abstract stop(): void;

    abstract seek(time: number): void;

    abstract getDuration(): number;

    abstract getPosition(): number;

    abstract setControls(bool: boolean): void;

    abstract setVolume(volume: number): void;

    abstract setPlaybackRate(rate: number): void;

    abstract getPlaybackRate(): number;

    abstract getVolume(): number;

    abstract getMuted(): boolean;

    abstract getApiConfig(): videojs.PlayerOptions | null;

    abstract getCurrentSource(): StreamingMediaSource;

    abstract setFullscreen(fullscreen: boolean): boolean;

    abstract getFullscreen(): boolean;

    abstract setMuted(muted: boolean): Promise<void> | void;

    abstract play(): Promise<void> | undefined;

    abstract pause(): void;

    abstract getPlaybackStats(): IPlaybackStats;

    abstract getPlaybackQuality(): VideoPlaybackQuality;

    abstract getCurrentDimensions(): { width?: number, height?: number };

    abstract toggleCinematicView(enabled: boolean): void;

    abstract getCinematicViewToggle(): CinematicViewToggle;

    abstract aspectRatio(aspectRatio: string): void

    abstract fill(enabled: boolean): void

    abstract fluid(enabled: boolean): void

    on(event: PlanetMediaPlayerEvents.Ready, fn: (event: ReadyEvent) => void, context?: any): this;
    on(event: PlanetMediaPlayerEvents | string, fn: (event: any) => void, context?: any): this;
    on(event: any, fn: (...args: any[]) => void, context?: any) {
        super.on(event, fn, context);
        return this;
    }
}
